import { ISKillsObject } from 'types/ISkill';
import HTML_ICON from 'assets/icons/skills/html-5.svg';
import CSS_ICON from 'assets/icons/skills/css-3.svg';
import JAVASCRIPT_ICON from 'assets/icons/skills/javascript.svg';
import TYPESCRIPT_ICON from 'assets/icons/skills/typescript.svg';
import TAILWIND_ICON from 'assets/icons/skills/tailwind.svg';
import BOOTSTRAP_ICON from 'assets/icons/skills/bootstrap.svg';
import CHAKRA_ICON from 'assets/icons/skills/chakra.svg';
import SCSS_ICON from 'assets/icons/skills/scss.svg';
import ANGULAR_ICON from 'assets/icons/skills/angular.svg';
import REACT_ICON from 'assets/icons/skills/react.svg';
import GITHUB_ICON from 'assets/icons/skills/github.svg';
import TFVC_ICON from 'assets/icons/skills/tfvc.svg';
import AWS_ICON from 'assets/icons/skills/aws.svg';
import DNS_ICON from 'assets/icons/skills/dns.svg';
import DOCKER_ICON from 'assets/icons/skills/docker.svg';
import GODADDY_ICON from 'assets/icons/skills/godaddy.svg';
import WEBPACK_ICON from 'assets/icons/skills/webpack.svg';
import BABEL_ICON from 'assets/icons/skills/babel.svg';

export const BASIC_SKILLS: ISKillsObject = {
  'Frontend Fundamentals': [
    {
      name: 'HTML',
      percentage: 90,
      yearsOfExperience: 5,
      image: HTML_ICON,
      description:
        'Using cutting-edge HTML5 elements: I intricately design dynamic and accessible web interfaces, showcasing a mastery of semantic markup and responsive design principles.',
    },
    {
      name: 'CSS',
      percentage: 90,
      yearsOfExperience: 5,
      image: CSS_ICON,
      description:
        'Harnessing the power of CSS Grid and Flexbox, I create visually stunning layouts with pixel-perfect precision. My proficiency extends to CSS variables and animations, ensuring a seamless blend of aesthetics and optimal user experience.',
    },
    {
      name: 'JavaScript',
      percentage: 90,
      yearsOfExperience: 5,
      image: JAVASCRIPT_ICON,
      description:
        'Leveraging the versatility of ES6+ syntax and modern JavaScript frameworks like React, I architect dynamic and interactive web applications. My proficiency extends to asynchronous programming, DOM manipulation, and seamless integration of APIs, showcasing a mastery of client-side scripting for a rich user experience.',
    },
    {
      name: 'TypeScript',
      percentage: 90,
      yearsOfExperience: 5,
      image: TYPESCRIPT_ICON,
      description:
        "Employing TypeScript's static typing and advanced features, I engineer robust and maintainable web applications. With a keen focus on scalability, I seamlessly integrate TypeScript into my development workflow, ensuring code clarity, enhanced developer productivity, and a more resilient software architecture.",
    },
  ],
  'SPA Frameworks & Libraries': [
    {
      name: 'Angular',
      percentage: 90,
      yearsOfExperience: 2,
      image: ANGULAR_ICON,
      description:
        "Leveraging the robust capabilities of Angular, I architect scalable and modular web applications. With a proficiency in TypeScript, I seamlessly integrate Angular's powerful features, such as dependency injection and two-way data binding, to create dynamic, maintainable, and feature-rich single-page applications, showcasing expertise in full-stack development.",
    },
    {
      name: 'React',
      percentage: 90,
      yearsOfExperience: 3,
      image: REACT_ICON,
      description:
        'Mastery of React enables me to build dynamic and efficient user interfaces, utilizing the virtual DOM and component-based architecture. With proficiency in JSX and state management libraries like Redux, I craft responsive and scalable applications, showcasing a keen understanding of modern front-end development and creating seamless user experiences.',
    },
  ],
  'CSS Frameworks, Libraries, and Extensions': [
    {
      name: 'Tailwind CSS',
      percentage: 90,
      yearsOfExperience: 3,
      image: TAILWIND_ICON,
      description:
        'Harnessing the utility-first approach of Tailwind CSS, I efficiently craft sleek and responsive user interfaces. By leveraging its extensive set of pre-built classes, I streamline the styling process, achieving a perfect balance between design flexibility and rapid development, resulting in visually appealing and highly maintainable codebases.',
    },
    {
      name: 'Bootstrap',
      percentage: 70,
      yearsOfExperience: 1,
      image: BOOTSTRAP_ICON,
      description:
        "Expertly utilizing Bootstrap's responsive grid system and extensive component library, I construct visually appealing and mobile-friendly web interfaces. By seamlessly integrating its pre-designed elements and styles, I ensure rapid development, consistent design, and cross-browser compatibility, showcasing proficiency in building modern and user-friendly websites.",
    },
    {
      name: 'Chakra UI',
      percentage: 70,
      yearsOfExperience: 3,
      image: CHAKRA_ICON,
      description:
        'Harnessing the power of Chakra UI, I design and develop stunning React applications with a focus on accessibility and design consistency. By leveraging its customizable and composable components, I ensure a seamless integration of design systems, enabling rapid prototyping and the creation of aesthetically pleasing, responsive, and accessible user interfaces.',
    },
    {
      name: 'SCSS',
      percentage: 90,
      yearsOfExperience: 3,
      image: SCSS_ICON,
      description:
        'Fluent in SCSS (Sass), I employ its advanced features like variables, nesting, and mixins to streamline and enhance the styling process. With a focus on maintainability and code organization, I create modular and scalable stylesheets, ensuring a clean separation of concerns and facilitating the efficient styling of complex web applications.',
    },
  ],
  'Source Control': [
    {
      name: 'GitHub',
      percentage: 90,
      yearsOfExperience: 5,
      image: GITHUB_ICON,
      description:
        'Proficient in Git and GitHub, I adeptly manage version control for collaborative software projects. Leveraging branches, pull requests, and commit history effectively, I ensure a streamlined development workflow, fostering collaboration, code integrity, and seamless integration of new features, showcasing expertise in source code management and collaboration tools.',
    },
    {
      name: 'TFVC',
      percentage: 90,
      yearsOfExperience: 2,
      image: TFVC_ICON,
      description:
        "With expertise in Team Foundation Version Control (TFVC), I proficiently manage version control for enterprise-level projects. Leveraging TFVC's centralized repository, branching strategies, and powerful change tracking, I ensure a robust and organized codebase. My skills include handling complex code merges, ensuring traceability, and facilitating collaboration in large-scale software development projects.",
    },
  ],
};

export const ADVANCED_SKILLS: ISKillsObject = {
  'Cloud & Infrastructure': [
    {
      name: 'Amazon Web Services',
      image: AWS_ICON,
      description:
        'Skilled in deploying, managing, and scaling applications on Amazon Web Services (AWS). Proficient in leveraging services like EC2, S3, and Lambda to ensure cost-efficient, secure, and high-performing cloud solutions.',
    },
    {
      name: 'DNS Management',
      image: DNS_ICON,
      description:
        'Experienced in managing DNS configurations, including domain setup, zone file management, and optimization for performance and reliability. Skilled in troubleshooting DNS issues and ensuring seamless connectivity.',
    },
    {
      name: 'Docker',
      image: DOCKER_ICON,
      description:
        'Proficient in containerization using Docker to streamline development and deployment workflows. Experienced in creating Dockerfiles, managing images, and orchestrating multi-container applications for consistent and efficient environments.',
    },
    {
      name: 'GoDaddy',
      image: GODADDY_ICON,
      description:
        "Experienced in domain and hosting management on GoDaddy's platform. Skilled in configuring DNS records, SSL certificates, and hosting environments for secure and reliable web applications.",
    },
  ],
  'Build Tools & Compilers': [
    {
      name: 'Webpack',
      image: WEBPACK_ICON,
      description:
        'Expert in using Webpack to bundle, optimize, and manage assets for modern web applications. Proficient in configuring loaders, plugins, and code splitting for efficient builds and improved performance.',
    },
    {
      name: 'Babel',
      image: BABEL_ICON,
      description:
        'Skilled in utilizing Babel for transpiling modern JavaScript into backwards-compatible code. Proficient in customizing configurations to optimize development workflows and ensure seamless compatibility across different browsers.',
    },
  ],
};

// #region Only Knowledge
/*
  {
    name: 'PHP',
    percentage: 50,
    yearsOfExperience: 0,
  },
  {
    name: 'Python',
    percentage: 60,
    yearsOfExperience: 0,
  },
  {
    name: 'Java',
    percentage: 50,
    yearsOfExperience: 0,
  },
  {
    name: 'C++',
    percentage: 60,
    yearsOfExperience: 0,
  },
  {
    name: 'Prolog',
    percentage: 50,
    yearsOfExperience: 0,
  },
  {
    name: 'Assembly',
    percentage: 50,
    yearsOfExperience: 0,
  },
  {
    name: 'PhotoShop',
    percentage: 90,
    yearsOfExperience: 0,
  },
  {
    name: 'Illustrator',
    percentage: 90,
    yearsOfExperience: 0,
  },
  {
    name: 'Blender',
    percentage: 50,
    yearsOfExperience: 0,
  },
  {
    name: 'Microsoft Office',
    percentage: 90,
    yearsOfExperience: 0,
  },
*/
// #endregion
